import { BookData } from '../interfaces/book-data.interface';
import { SpreadData } from '../interfaces/spread-data.interface';
import { CollageData } from '../interfaces/collage-data.interface';
import { Color } from '../interfaces/color.type';

export function setCollages(spread: SpreadData, collages: readonly CollageData[]): SpreadData {
  return { ...spread, collages };
}

/**
 * Заливка страницы — одна на весь разворот: цвет ложится во ВСЕ коллажи.
 * Белый = «заливки нет»: нормализация живёт здесь одна на всех, вызывающие отдают сырой hex.
 * Дженерик по обеим персист-формам — читается только состав коллажей.
 */
export function setSpreadFill<T extends { readonly collages: ReadonlyArray<{ fill?: Color | null }> }>(
  spread: T,
  fill: Color | null,
): T {
  const normalized = typeof fill === 'string' && fill.toLowerCase() === '#ffffff' ? null : fill;
  return { ...spread, collages: spread.collages.map(collage => ({ ...collage, fill: normalized })) } as T;
}

/** Удаляет спред по индексу. */
export function removeSpreadAtIndex(book: BookData, index: number): BookData {
  return { ...book, spreads: book.spreads.filter((_, i) => i !== index) };
}

/** Вставляет спред на позицию index+1 (после указанного спреда). */
export function insertSpreadAfter(book: BookData, index: number, spread: SpreadData): BookData {
  const newSpreads = [...book.spreads];
  newSpreads.splice(index + 1, 0, spread);
  return { ...book, spreads: newSpreads };
}

/** Перемещает спред с позиции fromIndex на позицию toIndex. */
export function moveSpreadTo(book: BookData, fromIndex: number, toIndex: number): BookData {
  const spreads = [...book.spreads];
  const [moved] = spreads.splice(fromIndex, 1);
  spreads.splice(toIndex, 0, moved);
  return { ...book, spreads };
}

/** Шаг на ±1: остаётся ради legacy fc-photobook-editor (вне gate-лейнов, компилируется против этого барреля). */
export function moveSpreadInBook(book: BookData, fromIndex: number, direction: 1 | -1): BookData {
  return moveSpreadTo(book, fromIndex, fromIndex + direction);
}

/** Пересчитывает ID спредов (1-based). Возвращает новую книгу + маппинг старых→новых ID. */
export function recalculateSpreadIds(book: BookData): { book: BookData; idMapping: Map<number, number> } {
  const idMapping = new Map<number, number>();
  let changed = false;
  const newSpreads = book.spreads.map((spread, index) => {
    const newId = index + 1;
    if (spread.id !== newId) {
      idMapping.set(spread.id, newId);
      changed = true;
      return { ...spread, id: newId };
    }

    return spread;
  });
  return {
    book: changed ? { ...book, spreads: newSpreads } : book,
    idMapping,
  };
}

/** Обновляет gap_percent книги. */
export function setGapPercent(book: BookData, gapPercent: number): BookData {
  return book.gap_percent === gapPercent ? book : { ...book, gap_percent: gapPercent };
}
